import React, { useEffect, useMemo, useState } from 'react'
import { Box, Typography, Divider, Accordion, AccordionSummary, AccordionDetails, IconButton } from '@mui/material'
import { ApiAutoComplete } from '../../../../../components/FormElements/ApiAutoComplete'
import FormInput from '../../../../../components/FormElements/FormInput'
import MultiSelectApiAutoComplete from '../../../../../components/FormElements/MultiSelectApiAutoComplete'
import { RenderTagsType } from '../../../../../components/FormElements/MultiSelectWithCheckBox'
import { Link } from 'react-router-dom'
import { black, blue, grey } from '../../../../../colors'
import ROUTES from '../../../../../consts/paths'
import GenericDateTimePicker from '../../../../../components/GenericDateTimePicker'
import AttributesMultiSelect from '../../../../../components/FormElements/AttributesMultiSelect'
import { ATTRIBUTE_TYPES } from '../../../../../consts'
import { useFormContext } from 'react-hook-form'
import { type ISection } from '../../../../../models/TermsSection'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import CloseIcon from '@mui/icons-material/Close'

interface InitialFormProps {
  isEditAction: boolean
  selectedRow?: ISection | Record<string, any>
  control: any
  readonly?: boolean
}

export const InitialForm: React.FC<InitialFormProps> = ({ isEditAction, selectedRow, control, readonly = false }) => {
  const { setValue, watch } = useFormContext()

  const [showTA, setShowTA] = useState(() => Boolean((selectedRow as any)?.teaching_assistants?.length))
  const [showAdditional, setShowAdditional] = useState(() => Boolean((selectedRow as any)?.additional_instructors?.length))

  const tasValue = watch('teachingAssistants') as unknown
  const hasSelectedTAs = useMemo(() => {
    if (!Array.isArray(tasValue)) return false
    return tasValue.length > 0
  }, [tasValue])

  const additionalInstructorsValue = watch('additionalInstructors') as unknown
  const hasSelectedAdditionalInstructors = useMemo(() => {
    if (!Array.isArray(additionalInstructorsValue)) return false
    return additionalInstructorsValue.length > 0
  }, [additionalInstructorsValue])

  // If TAs are already selected (e.g., edit flow), ensure the field is visible
  useEffect(() => {
    if (hasSelectedTAs) setShowTA(true)
  }, [hasSelectedTAs])

  // If additional instructors are already selected (e.g., edit flow), ensure the field is visible
  useEffect(() => {
    if (hasSelectedAdditionalInstructors) setShowAdditional(true)
  }, [hasSelectedAdditionalInstructors])

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <ApiAutoComplete
        apiEndpoint="/api/divisions/summary/?category_name=Course"
        readOnly={isEditAction}
        skip={readonly}
        required
        getOptionLabel={(option: any) => {
          const title = option?.title
          const abbreviation = option?.abbreviation
          if (title && abbreviation) return `${title} (${abbreviation})`
          return title ?? abbreviation ?? ''
        }}
        label="Course ID"
        labelField="abbreviation"
        name="courseId"
        valueField="abbreviation"
        getOptionSubLabel={(option => option?.categoryName)}
        selectedItem={selectedRow?.course}
        onChange={(option: any | null) => {
          if (!option) return
          const pickedCourseId = option?.abbreviation as string | undefined
          const pickedCourseTitle = option?.title as string | undefined
          const currentTitle = watch('title') as string | undefined
          const currentAbbreviation = watch('abbreviation') as string | undefined

          if (!currentTitle || currentTitle.trim().length === 0) {
            if (pickedCourseTitle) setValue('title', pickedCourseTitle, { shouldValidate: true, shouldDirty: true })
          }

          if (!currentAbbreviation || currentAbbreviation.trim().length === 0) {
            if (pickedCourseId) setValue('abbreviation', `${pickedCourseId}`, { shouldValidate: true, shouldDirty: true })
          }
        }}
      />

      <FormInput name="abbreviation" label="Section ID" control={control} required placeholder='Section ID' />

      {!isEditAction && (
        <Typography fontSize="14px" mt={1} color={grey[850]}>
          Best practice: Spell out the whole Course Section ID
        </Typography>
      )}

      <FormInput name="title" label="Title" control={control} required placeholder='Title' />
      <MultiSelectApiAutoComplete
        name='primaryInstructors'
        apiEndpoint='/api/users/summary/'
        sortField='name'
        apiExtraParams={{ isFaculty: true }}
        getOptionLabel={(option: any) => option.name}
        getOptionValue={(option: any) => option.userId}
        getOptionSubLabel={(option: any) => option.email}
        label='Instructor(s)'
        renderTagsType={RenderTagsType.Chips}
        skip={readonly}
      />

      <Typography fontSize="14px" color={grey[850]}>
        If the instructor does not exist, create a user profile &nbsp;
        <Link to={ROUTES.FACULTY_AND_STAFF} target="_blank" style={{ color: blue[500] }}>
          here.
        </Link>
      </Typography>

      {/* TA Dropdown */}
      {showTA && (
        <Box display="flex" alignItems="flex-start" gap={1}>
          <Box flex={1}>
            <MultiSelectApiAutoComplete
              name='teachingAssistants'
              apiEndpoint='/api/users/summary/'
              sortField='name'
              getOptionLabel={(option: any) => option.name}
              getOptionValue={(option: any) => option.userId}
              getOptionSubLabel={(option: any) => option.email}
              label='TA(s)'
              placeholder='Instructor Name'
              renderTagsType={RenderTagsType.Chips}
              skip={readonly}
            />
          </Box>
          <IconButton aria-label="Close TA(s)" size="medium" onClick={() => { setShowTA(false) }} sx={{ mt: 9 }}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>
      )}

      {/* Additional Instructor Dropdown */}
      {showAdditional && (
        <Box display="flex" alignItems="flex-start" gap={1}>
          <Box flex={1}>
            <MultiSelectApiAutoComplete
              name='additionalInstructors'
              apiEndpoint='/api/users/summary/'
              sortField='name'
              apiExtraParams={{ isFaculty: true }}
              getOptionLabel={(option: any) => option.name}
              getOptionValue={(option: any) => option.userId}
              getOptionSubLabel={(option: any) => option.email}
              label='Additional Instructor(s)'
              placeholder='Instructor Name'
              renderTagsType={RenderTagsType.Chips}
              skip={readonly}
            />
          </Box>
          <IconButton aria-label="Close Additional Instructor(s)" size="medium" onClick={() => { setShowAdditional(false) }} sx={{ mt: 9 }}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>
      )}

      {/* Add Other Roles Section (shown below dropdowns) */}
      {!((showTA || hasSelectedTAs) && (showAdditional || hasSelectedAdditionalInstructors)) && (
        <Box display="flex" alignItems="center" gap={3}>
          <Typography>Add Other Roles:</Typography>
          {!showTA && !hasSelectedTAs && (
            <Link
              to="#"
              style={{ color: blue[500], textDecoration: 'underline' }}
              onClick={e => { e.preventDefault(); setShowTA(true) }}
            >
              + TA
            </Link>
          )}
          {!showAdditional && !hasSelectedAdditionalInstructors && (
            <Link
              to="#"
              style={{ color: blue[500], textDecoration: 'underline' }}
              onClick={e => { e.preventDefault(); setShowAdditional(true) }}
            >
              + Additional Instructor
            </Link>
          )}
        </Box>
      )}
      <GenericDateTimePicker name="startTimestamp" label="Start Date" required />
      <GenericDateTimePicker name="endTimestamp" label="End Date" required />

      <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Additional Fields</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display='flex' flexDirection='column' gap={4}>
            <AttributesMultiSelect
              name="attributes"
              defaultAttributes={selectedRow?.attributes}
              extraParams={{ attributeType: ATTRIBUTE_TYPES.COURSE_ATTRIBUTES }}
              placeholder="Select attributes..."
              skip={readonly}
            />
            <Box>
              <FormInput name="institutionId" defaultValue={selectedRow?.institutionId}
                label="Institution ID" control={control} placeholder='Institution ID' />
              <Typography fontSize="14px" mt={1} color={grey[850]}>
                Identifier from your SIS or data warehouse. Changing this may break syncing with your institutional data.
              </Typography>
            </Box>
            <Box>
              <FormInput
                name="integrationSourceId"
                label="Integration Source ID"
                defaultValue={selectedRow?.integrationSourceId}
                control={control}
                placeholder='Integration Source ID'
              />
              <Typography fontSize="14px" mt={1} color={grey[850]}>
                Identifier from an external system (e.g. LMS). Changing this may break the connection to that system and any related functionality.
              </Typography>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}
